// @flow
import Switcher from '../controls/Switcher'

import _ from 'lodash'
import React, { Component } from 'react'
import { translateFunc } from '../../shared/localize'
import styled, { type StyledComponent } from 'styled-components'
import type {
  OptionType,
  Segment,
  Contact,
  Lookalike,
  ProviderIcons,
  ProviderTargetingSource
} from '../types'
import { Table, Cell, HeaderRow, HeaderCell, BodyRow } from '../MainTable'
import TargetingSourcesCoverage from '../controls/TargetingSourcesCoverage'
import { renderContactKindIcon } from '../TargetingSourcesTable'
import { segmentWithRelationsType } from '../constants'

const Wrapper: StyledComponent<Object, any, any> = styled.div`
  width: 100%;
  max-width: 850px;

  .targeting-sources-modal__ready {
    cursor: pointer;
  }
  .targeting-sources-modal__switcher {
    user-select: none;
    width: 10%;
    white-space: nowrap;
    font-size: 14px;
    display: table-cell;
    vertical-align: middle;
  }
`

type Props = {
  translations: Object,
  segments: Array<Segment>,
  contacts: Array<Contact>,
  lookalikeSegments: Array<Lookalike>,
  lookalikeContacts: Array<Lookalike>,
  providerTargetingSources: Array<ProviderTargetingSource>,
  includedSegmentIds: Array<number>,
  excludedSegmentIds: Array<number>,
  includedContactIds: Array<number>,
  excludedContactIds: Array<number>,
  includedLookalikeSegmentIds: Array<number>,
  includedLookalikeContactIds: Array<number>,
  includedProviderTargetingSourceIds: Array<number>,
  excludedProviderTargetingSourceIds: Array<number>,
  onClose: Function,
  onIncludedSegmentRemove: Function,
  onExcludedSegmentRemove: Function,
  onIncludedContactRemove: Function,
  onExcludedContactRemove: Function,
  onIncludedSegmentAdd: Function,
  onExcludedSegmentAdd: Function,
  onIncludedContactAdd: Function,
  onExcludedContactAdd: Function,
  onIncludedLookalikeSegmentRemove: Function,
  onIncludedLookalikeContactRemove: Function,
  onIncludedLookalikeSegmentAdd: Function,
  onIncludedLookalikeContactAdd: Function,
  onIncludedProviderTargetingSourceRemove: Function,
  onIncludedProviderTargetingSourceAdd: Function,
  onExcludedProviderTargetingSourceRemove: Function,
  onExcludedProviderTargetingSourceAdd: Function,
  icons: ProviderIcons
}

export default class Selector extends Component<Props> {
  t: Function
  selectOptions: Array<OptionType>
  selectLookalikeProviderSourcesOptions: Array<OptionType>

  constructor(props: Props) {
    super(props)

    this.t = translateFunc(props.translations)

    this.selectOptions = [
      { name: this.t('included'), id: 'included' },
      { name: this.t('excluded'), id: 'excluded' },
      { name: this.t('ignored'), id: 'ignored' }
    ]

    this.selectLookalikeProviderSourcesOptions = [
      { name: this.t('included'), id: 'included' },
      { name: this.t('ignored'), id: 'ignored' }
    ]
  }

  render() {
    const { onClose } = this.props

    return (
      <Wrapper className="targeting-sources-modal">
        <h2>{this.t('choose_sources')}</h2>
        <p>{this.t('choose_sources_hint')}</p>

        <h2>{this.t('segments_subtitle')}</h2>
        {this.renderSegmentsTable()}

        <h2>{this.t('contacts_subtitle')}</h2>
        {this.renderContactsTable()}

        <h2>{this.t('lookalikes_subtitle')}</h2>
        {this.renderLookalikesTable()}

        <h2>{this.t('provider_targeting_sources_subtitle')}</h2>
        {this.renderProviderTargetingSourcesTable()}

        <button
          className="blue-button targeting-sources-modal__ready"
          onClick={() => onClose && onClose()}
        >
          {this.t('ready')}
        </button>
      </Wrapper>
    )
  }

  renderSegmentsTable() {
    const {
      segments,
      includedSegmentIds,
      excludedSegmentIds,
      onIncludedSegmentAdd,
      onExcludedSegmentAdd,
      onIncludedSegmentRemove,
      onExcludedSegmentRemove
    } = this.props

    return (
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell width="20%" alignLeft>
              {this.t('name')}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {this.t('domain')}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {this.t('coverage')}
            </HeaderCell>
            <HeaderCell width="40%" alignLeft />
          </HeaderRow>
        </thead>
        <tbody>
          {_.map(segments, (segment) => {
            return (
              <BodyRow className="t-TargetingSourceRow" key={`segment_${segment.id}`}>
                <Cell className="t-TargetingSourceName">{segment.name}</Cell>
                <Cell>{segment.domain}</Cell>
                <Cell>
                  <TargetingSourcesCoverage icons={this.props.icons} coverage={segment.coverage} />
                </Cell>
                <Cell>
                  <div className="targeting-sources-modal__switcher">
                    {this.renderSwitcher(
                      segment.id,
                      includedSegmentIds,
                      excludedSegmentIds,
                      onIncludedSegmentAdd,
                      onExcludedSegmentAdd,
                      onIncludedSegmentRemove,
                      onExcludedSegmentRemove,
                      this.selectOptions
                    )}
                  </div>
                </Cell>
              </BodyRow>
            )
          })}
        </tbody>
      </Table>
    )
  }

  renderContactsTable() {
    const {
      contacts,
      includedContactIds,
      excludedContactIds,
      onIncludedContactAdd,
      onExcludedContactAdd,
      onIncludedContactRemove,
      onExcludedContactRemove
    } = this.props

    return (
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell width="20%" alignLeft>
              {this.t('name')}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {this.t('kind')}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {this.t('coverage')}
            </HeaderCell>
            <HeaderCell width="40%" alignLeft />
          </HeaderRow>
        </thead>
        <tbody>
          {_.map(contacts, (contact) => {
            return (
              <BodyRow className="t-TargetingSourceRow" key={`contact_${contact.id}`}>
                <Cell className="t-TargetingSourceName">{contact.name}</Cell>
                <Cell>{renderContactKindIcon(contact.kind)}</Cell>
                <Cell>
                  <TargetingSourcesCoverage icons={this.props.icons} coverage={contact.coverage} />
                </Cell>
                <Cell>
                  <div className="targeting-sources-modal__switcher">
                    {this.renderSwitcher(
                      contact.id,
                      includedContactIds,
                      excludedContactIds,
                      onIncludedContactAdd,
                      onExcludedContactAdd,
                      onIncludedContactRemove,
                      onExcludedContactRemove,
                      this.selectOptions
                    )}
                  </div>
                </Cell>
              </BodyRow>
            )
          })}
        </tbody>
      </Table>
    )
  }

  renderLookalikesTable() {
    const {
      lookalikeSegments,
      lookalikeContacts,
      includedLookalikeSegmentIds,
      includedLookalikeContactIds,
      onIncludedLookalikeSegmentAdd,
      onIncludedLookalikeSegmentRemove,
      onIncludedLookalikeContactAdd,
      onIncludedLookalikeContactRemove
    } = this.props

    return (
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell width="40%" alignLeft>
              {this.t('name')}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {this.t('coverage')}
            </HeaderCell>
            <HeaderCell width="40%" alignLeft />
          </HeaderRow>
        </thead>
        <tbody>
          {_.map(lookalikeSegments, (lookalikeSegment) => {
            return (
              <BodyRow
                className="t-TargetingSourceRow"
                key={`lookalikeSegment_${lookalikeSegment.id}`}
              >
                <Cell className="t-TargetingSourceName">{lookalikeSegment.name}</Cell>
                <Cell>
                  <TargetingSourcesCoverage
                    icons={this.props.icons}
                    coverage={lookalikeSegment.coverage}
                  />
                </Cell>
                <Cell>
                  <div className="targeting-sources-modal__switcher">
                    {this.renderSwitcher(
                      lookalikeSegment.id,
                      includedLookalikeSegmentIds,
                      [],
                      onIncludedLookalikeSegmentAdd,
                      null,
                      onIncludedLookalikeSegmentRemove,
                      null,
                      this.selectLookalikeProviderSourcesOptions
                    )}
                  </div>
                </Cell>
              </BodyRow>
            )
          })}
          {_.map(lookalikeContacts, (lookalikeContact) => {
            return (
              <BodyRow
                className="t-TargetingSourceRow"
                key={`lookalikeContact_${lookalikeContact.id}`}
              >
                <Cell className="t-TargetingSourceName">{lookalikeContact.name}</Cell>
                <Cell>
                  <TargetingSourcesCoverage
                    icons={this.props.icons}
                    coverage={lookalikeContact.coverage}
                  />
                </Cell>
                <Cell>
                  <div className="targeting-sources-modal__switcher">
                    {this.renderSwitcher(
                      lookalikeContact.id,
                      includedLookalikeContactIds,
                      [],
                      onIncludedLookalikeContactAdd,
                      null,
                      onIncludedLookalikeContactRemove,
                      null,
                      this.selectLookalikeProviderSourcesOptions
                    )}
                  </div>
                </Cell>
              </BodyRow>
            )
          })}
        </tbody>
      </Table>
    )
  }

  renderProviderTargetingSourcesTable() {
    const {
      providerTargetingSources,
      includedProviderTargetingSourceIds,
      excludedProviderTargetingSourceIds,
      onIncludedProviderTargetingSourceAdd,
      onIncludedProviderTargetingSourceRemove,
      onExcludedProviderTargetingSourceAdd,
      onExcludedProviderTargetingSourceRemove
    } = this.props

    return (
      <Table>
        <thead>
          <HeaderRow>
            <HeaderCell width="40%" alignLeft>
              {this.t('name')}
            </HeaderCell>
            <HeaderCell width="20%" alignLeft>
              {this.t('coverage')}
            </HeaderCell>
            <HeaderCell width="40%" alignLeft />
          </HeaderRow>
        </thead>
        <tbody>
          {_.map(providerTargetingSources, (providerTargetingSource) => {
            return (
              <BodyRow
                className="t-TargetingSourceRow"
                key={`providerTargetingSource_${providerTargetingSource.id}`}
              >
                <Cell className="t-TargetingSourceName">{providerTargetingSource.name}</Cell>
                <Cell>
                  <TargetingSourcesCoverage
                    icons={this.props.icons}
                    coverage={providerTargetingSource.coverage}
                  />
                </Cell>
                <Cell>
                  <div className="targeting-sources-modal__switcher">
                    {this.renderSwitcher(
                      providerTargetingSource.id,
                      includedProviderTargetingSourceIds,
                      providerTargetingSource.source_type === segmentWithRelationsType
                        ? []
                        : excludedProviderTargetingSourceIds,
                      onIncludedProviderTargetingSourceAdd,
                      providerTargetingSource.source_type === segmentWithRelationsType
                        ? null
                        : onExcludedProviderTargetingSourceAdd,
                      onIncludedProviderTargetingSourceRemove,
                      providerTargetingSource.source_type === segmentWithRelationsType
                        ? null
                        : onExcludedProviderTargetingSourceRemove,
                      providerTargetingSource.source_type === segmentWithRelationsType
                        ? this.selectLookalikeProviderSourcesOptions
                        : this.selectOptions
                    )}
                  </div>
                </Cell>
              </BodyRow>
            )
          })}
        </tbody>
      </Table>
    )
  }

  renderSwitcher(
    id: number,
    includedIds: Array<number>,
    excludedIds: Array<number>,
    onIncludedAdd: Function,
    onExcludedAdd: Function,
    onIncludedRemove: Function,
    onExcludedRemove: Function,
    selectOptions: Array<OptionType>
  ) {
    let switcherValue = this.switcherValue(id, includedIds, excludedIds)

    return (
      <Switcher
        value={switcherValue}
        options={selectOptions}
        onChange={(value) =>
          this.switcherChanged(
            switcherValue,
            value,
            id,
            onIncludedAdd,
            onExcludedAdd,
            onIncludedRemove,
            onExcludedRemove
          )
        }
      />
    )
  }

  switcherValue(id: number, includedIds: Array<number>, excludedIds: Array<number>) {
    if (_.includes(includedIds, id)) return 'included'
    if (_.includes(excludedIds, id)) return 'excluded'

    return 'ignored'
  }

  switcherChanged(
    oldValue: string,
    value: string,
    id: number,
    onIncludedAdd: Function,
    onExcludedAdd: Function,
    onIncludedRemove: Function,
    onExcludedRemove: Function
  ) {
    if (oldValue === 'included') {
      this.call(onIncludedRemove, id)
    } else if (oldValue === 'excluded') {
      this.call(onExcludedRemove, id)
    }
    if (value === 'included') {
      this.call(onIncludedAdd, id)
    } else if (value === 'excluded') {
      this.call(onExcludedAdd, id)
    }
  }

  call(func: Function, ...args: any) {
    func && func(...args)
  }
}
